import React, { useState } from "react";
import {
  Form,
  Space,
  Modal,
  Upload,
  Button,
  Table,
  message,
  Typography,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../../service/apiRequest";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/* -------------------------------------------------------------
   LEDGER COLUMNS — CLEANED + MATCHES YOUR BACKEND COLUMN FLOW
-------------------------------------------------------------- */
const ledgerKeys = [
  "sr.no",
  "name",
  "print_name",
  "code",
  "ledger_group",
  "credit_period",
  "mobile",
  "address_line_1",
  "address_line_2",
  "state",
  "country",
  "pin_code",
  "gst_no",
  "pan_no",
  "opening_balance",
  "cr_dr",
  "bank_name",
  "bank_account_no",
  "bank_ifsc",
  "fssai_no",
  "distance",
  "route",
  "station",
  "party_type",
  "dis_on_subtotal",
  "apply_tcs",
  "is_active",
];

/* -------------------------------------------------------------
   SAMPLE TEMPLATE — DEMO ROW WITH ALL KEYS
-------------------------------------------------------------- */
const sampleTemplate = [
  {
    "sr.no": 1,
    name: "Sample Ledger",
    print_name: "Sample Ledger",
    code: "LED001",
    ledger_group: "Sundry Debtors",
    credit_period: "30",
    mobile: "", // optional (defaults to 1234567890 if blank)
    address_line_1: "123 Main Street",
    address_line_2: "Near Clock Tower",
    state: "Rajasthan",
    country: "India",
    pin_code: "342001",
    gst_no: "08AAAAA0000A1Z5",
    pan_no: "AAAAA0000A",
    opening_balance: 1000,
    cr_dr: "Dr",
    bank_name: "HDFC Bank",
    bank_account_no: "50100000000000",
    bank_ifsc: "HDFC0000001",
    fssai_no: "",
    distance: "",
    route: "",
    station: "",
    party_type: "Customer",
    dis_on_subtotal: "0",
    apply_tcs: "No",
    is_active: "Yes",
  },
];

const ImportLedgerModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  /* -------------------------------------------------------------
     DOWNLOAD SAMPLE TEMPLATE
  -------------------------------------------------------------- */
  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LedgerTemplate");
    XLSX.writeFile(wb, "ledger_import_template.xlsx");
  };

  /* -------------------------------------------------------------
     UPLOAD EXCEL FILE
  -------------------------------------------------------------- */
  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!json.length) {
        message.warning("Excel file is empty.");
        return false;
      }

      setParsedData(json);
    } catch (error) {
      console.error(error);
      message.error("Failed to read Excel file. Please check format.");
    } finally {
      setLoading(false);
    }

    return false;
  };

  /* -------------------------------------------------------------
     SUBMIT IMPORT
  -------------------------------------------------------------- */
  const handleImport = async () => {
    if (!parsedData.length) {
      return message.error("No data to import.");
    }

    try {
      await apiRequest({
        method: "post",
        url: "/import-ledgers",
        data: parsedData,
      });

      message.success("Ledgers imported successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      message.error("Import failed. Check data formatting.");
    }
  };

  return (
    <Modal
      title="Import Ledger"
      width="90%"
      open={visible}
      onCancel={onClose}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0, loading }}
      destroyOnHidden
    >
      <Form layout="vertical">
        {/* STEP 1 – DOWNLOAD TEMPLATE */}
        <Form.Item label="Step 1">
          <Space direction="vertical" size={4}>
            <Text type="secondary">
              Download the sample template and fill it with ledger data.
            </Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        {/* STEP 2 – UPLOAD FILE */}
        <Form.Item label="Step 2">
          <Space direction="vertical" size={4}>
            <Text type="secondary">Upload your filled Excel file below.</Text>
            <Upload
              accept=".xlsx"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {/* PREVIEW TABLE */}
      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: "block" }}>Preview:</Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, index) => index!.toString()}
            size="small"
            scroll={{ y: 300, x: "max-content" }}
            pagination={false}
            columns={Object.keys(parsedData[0] || {}).map((key) => ({
              title: key,
              dataIndex: key,
              key,
            }))}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportLedgerModal;
